import {
    getStartOfDayInMilliseconds,
    isThisWeek,
    isTomorrow,
    isToday,
    isThisEvening,
    isTonight,
    wasLastNight,
    wasYesterday,
    wasLastWeek
} from '@dazn/lp-web/src/utils/timeUtils';
import {tileLabel} from '@dazn/lp-web/src/constants/resourceStrings.constants';
import {tileStatus, tileTypes} from '@dazn/lp-web/src/constants/tile.constants';
import getDateFragments from '@dazn/lp-web/src/utils/getDateFragments';
import {getDictionaryString} from '@dazn/lp-web/src/utils/dictionary';

const getLiveKey = () => tileLabel.IS_LIVE;

const getCatchupKey = (msTileStart: number, msStartOfDay: number) => {
    if (isToday(msTileStart, msStartOfDay)) {
        return tileLabel.WAS_TODAY;
    }

    if (wasLastNight(msTileStart, msStartOfDay)) {
        return tileLabel.WAS_LAST_NIGHT;
    }

    if (wasYesterday(msTileStart, msStartOfDay)) {
        return tileLabel.WAS_YESTERDAY;
    }

    if (wasLastWeek(msTileStart, msStartOfDay)) {
        return tileLabel.WAS_LAST_WEEK;
    }

    return tileLabel.WAS_OVER_WEEK_AGO;
};

const getUpcomingKey = (
    status: string,
    msTileStart: number,
    msStartOfDay: number
) => {
    const suffix = status === tileStatus.EMBARGOED ? '_EMBARGOED' : '';
    if (isToday(msTileStart, msStartOfDay)) {
        return status === tileStatus.EMBARGOED
            ? tileLabel.IS_TODAY_EMBARGOED
            : tileLabel.WATCH_LIVE_TODAY;
    }

    if (isThisEvening(msTileStart, msStartOfDay)) {
        return tileLabel[`IS_THIS_EVENING${suffix}`];
    }

    if (isTonight(msTileStart, msStartOfDay)) {
        return tileLabel[`IS_TONIGHT${suffix}`];
    }

    if (isTomorrow(msTileStart, msStartOfDay)) {
        return tileLabel[`IS_TOMORROW${suffix}`];
    }

    if (isThisWeek(msTileStart, msStartOfDay)) {
        return tileLabel[`IS_THIS_WEEK${suffix}`];
    }

    return tileLabel[`IS_OVER_WEEK_AWAY${suffix}`];
};

function isTodayRedesign(date: Date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date?.toDateString() === today.toDateString();
}

function isYesterdayRedesign(date: Date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to midnight of today

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1); // Move to the previous day

    return date?.toDateString() === yesterday.toDateString();
}

function isTomorrowRedesign(date: Date) {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    return date.toDateString() === tomorrow.toDateString();
}

function isBetweenTodayAndSixDays(date: Date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const sixDaysFromNow = new Date(today);
    sixDaysFromNow.setDate(sixDaysFromNow.getDate() + 6);

    return date > today && date <= sixDaysFromNow;
}

function isSameYearPastDaysRedesign(date: Date) {
    const currentYear = new Date().getFullYear();
    const eventYear = new Date(date).getFullYear();
    return currentYear === eventYear && isBeforeToday(date);
}

function isPreviousYearRedesign(date: Date) {
    const currentYear = new Date().getFullYear();
    const eventYear = new Date(date).getFullYear();
    return currentYear !== eventYear && eventYear < currentYear;
}

function isBeforeToday(date: Date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
}

const getRedesignKey = (tileStartDate: Date) => {
    if (isPreviousYearRedesign(tileStartDate)) {
        return tileLabel.DATE_YEAR;
    }

    if (isSameYearPastDaysRedesign(tileStartDate)) {
        return tileLabel.CATCHUP_REDESIGN;
    }

    if (isYesterdayRedesign(tileStartDate)) {
        return tileLabel.YESTERDAY;
    }

    if (isTodayRedesign(tileStartDate)) {
        return tileLabel.TODAY_TIME;
    }

    if (isTomorrowRedesign(tileStartDate)) {
        return tileLabel.TOMORROW_TIME;
    }

    if (isBetweenTodayAndSixDays(tileStartDate)) {
        return tileLabel.DAY_TIME;
    }

    return tileLabel.UPCOMING_REDESIGN;
};

const getLiveRedesignKey = () => {
    return;
};

const tileDateToStringGetter =
    (
        getDateFragments: any,
        getUpcomingKey: any,
        getCatchupKey: any,
        getLiveKey: any,
        getDictionaryString: any
    ) =>
    (tileStartDate: Date, tileType: string, status: string) => {
        let translationKey;
        const dateFragments = getDateFragments(tileStartDate);
        const msTileStart = tileStartDate.getTime();
        switch (tileType) {
            case tileTypes.UPCOMING:
                translationKey = getUpcomingKey(
                    status,
                    msTileStart,
                    getStartOfDayInMilliseconds()
                );
                break;
            case tileTypes.CATCH_UP:
            case tileTypes.HIGHLIGHTS:
            case tileTypes.COACHES:
            case tileTypes.CONDENSED:
            case tileTypes.AMERICAN_FOOTBALL_CONDENSED:
            case tileTypes.AMERICAN_FOOTBALL_GAME_IN_40:
            case tileTypes.FIBA_TEN_MINUTES_HIGHLIGHTS:
            case tileTypes.FIBA_THREE_MINUTES_HIGHLIGHTS:
                translationKey = getCatchupKey(
                    msTileStart,
                    getStartOfDayInMilliseconds()
                );
                break;
            case tileTypes.LIVE:
                translationKey = getLiveKey();
                break;
            default:
                translationKey = tileLabel.FALLBACK;
        }

        return getDictionaryString(translationKey, dateFragments);
    };

const getTileDateLabel = (tileStartDate: Date, tileType: string) => {
    let translationKey;
    const dateFragments = getDateFragments(tileStartDate);
    switch (tileType) {
        case tileTypes.UPCOMING:
            translationKey = getRedesignKey(tileStartDate);
            break;
        case tileTypes.CATCH_UP:
        case tileTypes.HIGHLIGHTS:
        case tileTypes.COACHES:
        case tileTypes.CONDENSED:
        case tileTypes.AMERICAN_FOOTBALL_CONDENSED:
        case tileTypes.AMERICAN_FOOTBALL_GAME_IN_40:
        case tileTypes.FIBA_TEN_MINUTES_HIGHLIGHTS:
        case tileTypes.FIBA_THREE_MINUTES_HIGHLIGHTS:
            translationKey = getRedesignKey(tileStartDate);
            break;
        case tileTypes.LIVE:
            translationKey = getLiveRedesignKey();
            break;
        default:
            translationKey = getRedesignKey(tileStartDate);
    }

    return getDictionaryString(translationKey as string, dateFragments);
};

const getTileDateToString = tileDateToStringGetter(
    getDateFragments,
    getUpcomingKey,
    getCatchupKey,
    getLiveKey,
    getDictionaryString
);

export {
    getTileDateToString as default,
    getTileDateLabel,
    getLiveKey,
    getCatchupKey,
    getUpcomingKey,
    tileDateToStringGetter,
    isTodayRedesign
};
